import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo } from "react";

import { perDay, pickWord, reserveChips } from "@/lib/format";
import type { Row } from "@/lib/providers";

/**
 * Таблица отчёта.
 *
 * Виртуализации нет намеренно, в отличие от соседа: там 3 158 партий, здесь
 * строк сотни — ёмкость зоны ограничена сверху самим API. Прокручиваемый
 * контейнер известной высоты у таблицы есть, поэтому включить виртуализацию,
 * если отчёт когда-нибудь вырастет, — правка на десяток строк, а не
 * пересборка экрана.
 *
 * На узком экране та же разметка перекладывается в карточки: строки и ячейки
 * становятся блоками, а подпись колонки приезжает из `data-label` через
 * `::before`. Второго набора шаблонов не появляется — расходиться им негде.
 */

/** Ячейка-колонка чисел: разряд под разрядом, иначе колонку не сравнить глазом. */
const NUM = "text-right tnum";

const column = createColumnHelper<Row>();

/** Тон бейджа сегмента. Буква несёт смысл сама, цвет только усиливает. */
const ABC_TONE: Record<string, string> = {
  A: "bg-ok-bg border-ok/60",
  B: "bg-warn-bg border-warn/60",
  C: "bg-neutral-bg border-border-strong",
};

export function PlacementTable({ rows, days }: { rows: Row[]; days: number }) {
  const columns = useMemo(
    () => [
      column.accessor("rank", {
        header: "#",
        meta: { align: NUM, cell: "text-fg-muted" },
      }),
      // Номер короба — то, чем короб зовут на складе. Стоит перед штрихкодом:
      // сотрудник ищет глазами именно его.
      column.accessor("sort_order", {
        header: "Номер короба",
        meta: { align: NUM, cell: "font-medium" },
      }),
      column.accessor("box_barcode", {
        header: "Короб",
        // Штрихкод, и только он. Историческое имя («Невиномыск Невиномыск
        // Короб 7441») адресом не является и занимало вторую строку в каждой
        // ячейке.
        cell: (c) => <span className="tnum">{c.getValue()}</span>,
      }),
      column.accessor("korob_barcode", {
        header: "Код ВБ",
        cell: (c) => <span className="tnum text-fg-muted">{c.getValue()}</span>,
      }),
      // Картинка вместо названия. Названия у карточек одной модели совпадают
      // дословно и различить товар не помогают; фотография узнаётся с одного
      // взгляда, а номера рядом дают точность.
      column.accessor("image_url", {
        header: "Фото",
        cell: (c) =>
          c.getValue() ? (
            /* Ссылка на CDN маркетплейса напрямую: файл грузит браузер, сервис
               картинки не проксирует и не хранит. `referrerpolicy` — чтобы
               вместе с запросом наружу не уезжал адрес внутренней страницы. */
            <img
              src={c.getValue()}
              alt={c.row.original.product}
              title={c.row.original.product}
              loading="lazy"
              referrerPolicy="no-referrer"
              className="h-9 w-7 rounded-[3px] border border-border object-cover"
              onError={(e) => {
                // Битая ссылка не должна оставлять значок сломанной картинки:
                // место под фото остаётся пустым, строка не съезжает.
                e.currentTarget.style.visibility = "hidden";
              }}
            />
          ) : null,
      }),
      // Модель — то, чем товар зовут; карточка — конкретный размер этой
      // модели. Оба номера видны, потому что по ним устроен фильтр.
      column.accessor("model_id", {
        header: "Модель",
        meta: { align: NUM },
        cell: (c) => (
          <span className="flex flex-col items-end">
            <span className="font-medium tnum">{c.getValue()}</span>
            <span className="text-[11px] tnum text-fg-muted">
              карт. {c.row.original.card_id}
            </span>
          </span>
        ),
      }),
      column.accessor("product_barcode", {
        header: "Баркод",
        cell: (c) => (
          <span className="flex flex-col">
            <span className="tnum">{c.getValue()}</span>
            {c.row.original.size ? (
              <span className="text-[11px] text-fg-muted">
                размер {c.row.original.size}
              </span>
            ) : null}
          </span>
        ),
      }),
      // Решение о переносе принимают по среднему в день, поэтому крупно
      // именно оно. Всего за период — подписью: 20.6 в день за три дня и за
      // девяносто это разная надёжность оценки, и без знаменателя они
      // выглядели бы одинаково.
      column.accessor("picks_per_day", {
        header: "Отборов/день",
        meta: { align: NUM },
        cell: (c) => (
          <span className="flex flex-col items-end">
            <span className="font-medium tnum">{perDay(c.getValue())}</span>
            <span className="text-[11px] tnum text-fg-muted">
              {c.row.original.picks}
              <span className="sr-only">
                {" "}
                {pickWord(c.row.original.picks)}
              </span>{" "}
              за {days} дн
            </span>
          </span>
        ),
      }),
      // Буква несёт смысл сама; цвет её только усиливает. Так сегмент
      // читается и на монохромном экране, и тем, кто не различает зелёный с
      // янтарным.
      column.accessor("abc", {
        header: "ABC",
        cell: (c) => (
          <span
            className={
              "inline-grid size-[22px] place-items-center rounded-[5px] border text-[11px] font-semibold " +
              (ABC_TONE[c.getValue()] ?? ABC_TONE["C"])
            }
          >
            {c.getValue()}
          </span>
        ),
      }),
      column.accessor("qty_in_box", {
        header: "Штук",
        meta: { align: NUM },
        cell: (c) => (
          <span className="tnum">
            {c.getValue()}
            <span className="text-fg-muted">
              {" "}
              из {c.row.original.qty_total}
            </span>
          </span>
        ),
      }),
      // Отвечает на первый вопрос сотрудника «где ещё этот товар?». Пусто —
      // донор опустеет, подносить неоткуда; номера — готовый адрес, куда
      // идти. Ближний короб первый.
      column.accessor("reserve_boxes", {
        header: "Резерв",
        cell: (c) => {
          const nums = c.getValue();
          if (nums.length === 0)
            return (
              <span className="text-[11.5px] text-fg-muted">
                единственный короб
              </span>
            );
          const chips = reserveChips(nums);
          return (
            /* Чипы переносятся по строкам, а не клипаются: коллекцию чипов
               обрезать нельзя, остаток обязан оставаться доступным. */
            <span className="flex flex-wrap gap-1">
              {chips.shown.map((n) => (
                <span
                  key={n}
                  className="rounded border border-border bg-bg-elevated px-1.5 py-px text-[11.5px] tnum"
                >
                  {n}
                </span>
              ))}
              {chips.rest > 0 ? (
                <span
                  title={nums.join(", ")}
                  className="rounded border border-border-strong px-1.5 py-px text-[11.5px] tnum text-fg-muted"
                >
                  +{chips.rest}
                </span>
              ) : null}
            </span>
          );
        },
      }),
      column.accessor("coverage_pct", {
        header: "Покрытие",
        meta: { align: NUM },
        cell: (c) => (
          <span className="flex flex-col items-end gap-1">
            <span className="tnum">{c.getValue()}%</span>
            {/* Полоса показывает не саму строку, а форму кривой: где она
                перестаёт расти, там следующие короба уже почти ничего не
                добавляют. Число рядом, полоса его не заменяет. */}
            <span
              aria-hidden="true"
              className="block h-1 w-full min-w-[52px] overflow-hidden rounded-full bg-bg-hover"
            >
              <span
                className="block h-full bg-accent"
                style={{ width: `${Math.min(c.getValue(), 100)}%` }}
              />
            </span>
          </span>
        ),
      }),
    ],
    [days],
  );

  const table = useReactTable({
    data: rows,
    columns,
    // Строку опознаёт пара «короб + товар»: в одном коробе несколько товаров,
    // и по одному штрихкоду короба строки неразличимы.
    getRowId: (row) => row.box_barcode + "·" + row.product_barcode,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div
      className="min-h-0 flex-1 overflow-auto rounded-[var(--radius-card)] border border-border bg-bg-subtle"
      data-component="PlacementTable"
      data-annot="таблица"
    >
      <table className="w-full border-collapse text-[13px]">
        <caption className="sr-only">
          Короба, рекомендованные к переносу в зону быстрого доступа, по
          убыванию спроса
        </caption>
        <thead className="hidden md:table-header-group">
          {table.getHeaderGroups().map((group) => (
            <tr key={group.id}>
              {group.headers.map((header) => (
                <th
                  key={header.id}
                  scope="col"
                  className={
                    "sticky top-0 z-10 whitespace-nowrap border-b border-border bg-bg-elevated px-2.5 py-2 text-[11px] font-medium uppercase tracking-[0.04em] text-fg " +
                    ((header.column.columnDef.meta as { align?: string })
                      ?.align ?? "text-left")
                  }
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className="block md:table-row-group">
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              data-box={row.original.box_barcode}
              data-product={row.original.product_barcode}
              data-annot={`строка ${row.original.sort_order}`}
              className="mb-2 block rounded-[var(--radius-ui)] border border-border bg-bg p-2 md:mb-0 md:table-row md:rounded-none md:border-0 md:border-b md:border-border md:bg-transparent md:p-0 md:hover:bg-bg-hover"
            >
              {row.getVisibleCells().map((cell) => {
                const meta = cell.column.columnDef.meta as {
                  align?: string;
                  cell?: string;
                };
                return (
                  <td
                    key={cell.id}
                    data-label={String(cell.column.columnDef.header)}
                    data-annot={String(cell.column.columnDef.header)}
                    className={
                      "flex items-center justify-between gap-3 border-b border-border/60 py-1 last:border-0 " +
                      "before:text-[11px] before:uppercase before:tracking-[0.04em] before:text-fg-muted before:content-[attr(data-label)] " +
                      "md:table-cell md:border-0 md:px-2.5 md:py-1.5 md:before:content-none " +
                      (meta?.align ?? "") +
                      " " +
                      (meta?.cell ?? "")
                    }
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
